import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, next } from '@ember/runloop';
import { service } from '@ember/service';

import type { Editor as TinymceEditor, Ui } from 'tinymce';

import type { BlockThemeConfig, PopupListItem, Service } from 'webapp-types';
import ThemeConfig from 'webapp/const/theme-config';
import type { StoryElementType } from 'webapp/enums/enums';
import {
	BillingFeature,
	BlockRenderStep,
	BlockVisualization,
	DataStoryType,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import DataStoryBaseComponent from 'webapp/components/data-story/base';
import type FormHtmlEditorComponent from 'webapp/components/form/html-editor';
import type { HtmlEditorOptions } from 'webapp/components/form/html-editor';
import FormHtmlEditorFullComponent from 'webapp/components/form/html-editor-full';

import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';
import type DataSelect from 'webapp/models-custom/data-select';
import type StoryElementBase from 'webapp/models-custom/story-element/base';
import type { ReportsChangeDataStorySettingsTour } from 'webapp/models-custom/tour/reports';
import {
	ReportsAddDataElementTour,
	ReportsAddDataStoryTour,
	ReportsSelectDataElementTour,
	ReportsSelectDataStoryTour,
} from 'webapp/models-custom/tour/reports';
import type VisualizationDataStory from 'webapp/models-custom/visualization/data-story';

enum CommandPopupAction {
	// TinyMCE commands
	INSERT_TABLE = 'insert-table',
	INSERT_LINK = 'insert-link',
	INSERT_DIVIDER = 'insert-divider',
	INSERT_EMOTICON = 'insert-emoticon',

	// data-story actions
	ADD_STORY_PREFIX = 'add-story-',
	ADD_STORY_NEUTRAL = 'add-story-neutral',
	ADD_STORY_GOOD = 'add-story-good',
	ADD_STORY_OK = 'add-story-ok',
	ADD_STORY_BAD = 'add-story-bad',

	// story-element dynamic action patterns
	ADD_METRIC_PREFIX = 'add-metric-',
	ADD_ELEMENT_PREFIX = 'add-element-',
}

// tinymce types onSetup as returning a teardown, and keeps a no-op when none is returned
const noTeardown = () => undefined;

interface Args {
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	themeData: BlockThemeConfig.BlockParsed;
	isUnselectable: boolean;
	canEdit: boolean;
}

export default class BlockContentDataStoryComponent<
	T extends Args,
> extends DataStoryBaseComponent<T> {
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('tour') declare tourService: Service.Tour;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('billing') declare billingService: Service.Billing;

	constructor(owner: Owner, args: T) {
		super(owner, args);
		void this.mmReportsService.loadFonts();
	}

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization(): VisualizationDataStory {
		return this.blockSetup.visualization as VisualizationDataStory;
	}

	// ----- when readonly ------

	get sanitizedText() {
		const text = this.visualization.shownText || '&nbsp;';
		return this.dataStoryService.sanitizeTextWithStoryElements(text);
	}

	// ----- when editable ------

	editor: TinymceEditor | null = null;

	tour:
		| ReportsAddDataStoryTour
		| ReportsSelectDataStoryTour
		| ReportsChangeDataStorySettingsTour
		| ReportsAddDataElementTour
		| ReportsSelectDataElementTour
		| null = null;

	get isStoryButtonDisabled() {
		// disable story only on reports. on boards, the block itself will have overlay
		return this.block.isFromReport && !this.billingService.visualizationsAdvancedEnabled;
	}

	get centerByDefault() {
		const fragment = this.block.reportSlideFragment;
		return !!fragment?.slide?.isTypeCover && !fragment.isOnPagelessReport;
	}

	get isInputFocused() {
		return this.block.reportSlideFragment?.isNewlyCreated;
	}

	get editorOptions(): HtmlEditorOptions {
		const isDistractionFree = this.block.isFromReport;

		return Object.assign({}, this.dataStoryService.defaultEditorOptions, {
			centerByDefault: this.centerByDefault,
			isInputFocused: this.isInputFocused,
			isDistractionFree: isDistractionFree,
			toolbar: isDistractionFree ? false : this.dataStoryService.defaultEditorOptions.toolbar,
			placeholder: isDistractionFree
				? 'Write something, or press "/" for commands'
				: 'Click to add text...',
			onSetup: (_component: FormHtmlEditorComponent, editor: TinymceEditor) => {
				const getButtonItemsFn = this.getButtonItems;
				this.editor = editor;
				// only add story button in non-distraction-free mode
				if (!isDistractionFree) {
					this.dataStoryService.addStoryButtonToEditor(
						editor,
						getButtonItemsFn,
						this.args.themeData.colors,
					);
				}
			},
			commandPopupList: {
				itemsList: this.commandPopupOptions,
				doAction: (actionName?: string) => {
					if (this.editor) {
						this.executeCommandPopupAction(this.editor, actionName || '');
					}
				},
			},
		});
	}

	get dataStoryOptions(): {
		id: string;
		text: string;
		icon: IconName;
		tinymceIcon: string;
		color: string;
		type?: DataStoryType;
		action?: string;
	}[] {
		return [
			{
				id: 'story-neutral',
				text: "We're at... compared to...",
				icon: 'CircleFill',
				tinymceIcon: 'circle-grey',
				color: '#8e929b',
				type: this.visualization.defaultStoryType,
				action: CommandPopupAction.ADD_STORY_NEUTRAL,
			},
			{
				id: 'story-good',
				text: "We're doing great. We're at... compared to...",
				icon: 'CircleFill',
				tinymceIcon: 'circle-green',
				color: '#02b875',
				type: DataStoryType.POSITIVITY_GOOD,
				action: CommandPopupAction.ADD_STORY_GOOD,
			},
			{
				id: 'story-ok',
				text: "We're doing okay. We're at... compared to...",
				icon: 'CircleFill',
				tinymceIcon: 'circle-orange',
				color: '#f78d1b',
				type: DataStoryType.POSITIVITY_OK,
				action: CommandPopupAction.ADD_STORY_OK,
			},
			{
				id: 'story-bad',
				text: "Not the best results as we're at... compared to...",
				icon: 'CircleFill',
				tinymceIcon: 'circle-red',
				color: '#f76868',
				type: DataStoryType.POSITIVITY_BAD,
				action: CommandPopupAction.ADD_STORY_BAD,
			},
		];
	}

	get commandPopupOptions(): PopupListItem<string>[] {
		const options: PopupListItem<string>[] = [
			{
				id: 'data-story',
				text: 'Data story',
				icon: 'DataStory',
				sublistItems: this.dataStoryOptions.map((option) => ({
					id: option.id,
					text: option.text,
					icon: option.icon,
					color: option.color,
					action: option.action,
				})),
			},
			{
				id: 'story-element',
				text: 'Story element',
				icon: 'Sparkles',
				sublistItems: this.generateDataSelectSublistItems(),
			},
			{
				id: 'table',
				text: 'Table',
				icon: 'Table',
				action: CommandPopupAction.INSERT_TABLE,
			},
			{
				id: 'link',
				text: 'Link',
				icon: 'Link',
				action: CommandPopupAction.INSERT_LINK,
			},
			{
				id: 'divider',
				text: 'Divider',
				icon: 'Divider',
				action: CommandPopupAction.INSERT_DIVIDER,
			},
			{
				id: 'emoticon',
				text: 'Emoji',
				icon: 'MoodSmile',
				action: CommandPopupAction.INSERT_EMOTICON,
			},
		];

		return options;
	}

	private createNewStoryFragmentAndAddToEditor(type: DataStoryType): void {
		this.visualization
			.addMetric()
			.then((dsId) => {
				this.addStoryToEditor(dsId, type);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	private getButtonItems() {
		const editor = this.editor;

		if (!editor) {
			return [];
		}

		if (this.isStoryButtonDisabled) {
			this.billingService.handleLimitations(BillingFeature.VISUALIZATIONS_ADVANCED);
			return [];
		}

		// save content so we don't show just deleted stories in dropdown
		this.onChange(editor.getContent());

		const items: Ui.Menu.NestedMenuItemContents[] = [];

		items.push({
			type: 'nestedmenuitem',
			text: 'Add Data story',
			onSetup: () => {
				if (this.tour instanceof ReportsAddDataStoryTour) {
					later(() => {
						this.tour?.goToStep(2);
					}, 500);
				}
				return noTeardown;
			},
			getSubmenuItems: () => {
				// convert common data structure to TinyMCE menu items
				return this.dataStoryOptions.map((option) => ({
					type: 'menuitem',
					text: option.text,
					icon: option.tinymceIcon,
					onSetup:
						option.id === 'story-neutral'
							? () => {
									if (this.tour instanceof ReportsSelectDataElementTour) {
										if (this.tour.isActive) {
											this.tour?.hideBubble();
											this.tour = null;
										}
									}

									if (this.tour instanceof ReportsAddDataStoryTour) {
										this.endTour();
									}

									later(() => {
										this.startEligibleTour([TourId.REPORTS__SELECT_DATA_STORY]);
									}, 500);
									return noTeardown;
								}
							: undefined,
					onAction: () => {
						this.createNewStoryFragmentAndAddToEditor(option.type!);
						this.completeSelectDataStoryTour();
					},
				}));
			},
		});

		// add "Metric elements" items
		items.push({
			type: 'nestedmenuitem',
			text: 'Metric elements',
			onSetup: () => {
				if (this.tour instanceof ReportsAddDataElementTour) {
					later(() => {
						this.tour?.goToStep(2);
					}, 500);
				}
				return noTeardown;
			},
			getSubmenuItems: () => {
				const subItems: Ui.Menu.NestedMenuItemContents[] = [];

				// add existing data selects
				this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
					const sublistData = this.getDataSelectSublistData(ds);

					subItems.push({
						type: 'nestedmenuitem',
						text: sublistData.text,
						icon: `circle${sublistData.color}`,
						getSubmenuItems: () => {
							return this.dataStoryService.storyElementOrderInTinyMceDropdown.map(
								(type) => {
									const text = this.dataStoryService.getStoryElementTitle(type);
									return {
										type: 'menuitem',
										text,
										onAction: () => {
											this.addStoryElementToEditor(type, ds.numericId!);
										},
									};
								},
							);
						},
					});
				});

				// add "New metric" option
				subItems.push({
					type: 'nestedmenuitem',
					text: 'New metric',
					icon: 'plusGreen',
					onSetup: () => {
						// this one is not in a loop so we put the event on this
						if (this.tour instanceof ReportsSelectDataStoryTour) {
							if (this.tour.isActive) {
								this.tour?.hideBubble();
								this.tour = null;
							}
						}

						if (this.tour instanceof ReportsAddDataElementTour) {
							this.endTour();
						}

						later(() => {
							this.startEligibleTour([TourId.REPORTS__SELECT_DATA_ELEMENT]);
						}, 500);
						return noTeardown;
					},
					getSubmenuItems: () => {
						const subSubItems: Ui.Menu.NestedMenuItemContents[] = [];

						this.dataStoryService.storyElementOrderInTinyMceDropdown.forEach((type) => {
							const text = this.dataStoryService.getStoryElementTitle(type);
							subSubItems.push({
								type: 'menuitem',
								text,
								onAction: () => {
									this.visualization
										.addMetric()
										.then((dsId) => {
											this.addStoryElementToEditor(type, dsId);
										})
										.catch(
											this.requestService.showAllValidationErrors.bind(
												this.requestService,
											),
										);
								},
							});
						});

						return subSubItems;
					},
				});

				return subItems;
			},
		});

		return items;
	}

	private getDataSelectSublistData(ds: DataSelect): {
		text: string;
		icon: IconName;
		color?: string | number;
		dsId: number;
	} {
		const colorMap = this.visualization.colorMap;
		let text = ds.shownTitle;

		// shorten the text...
		if (text.length > 50) {
			text = text.substring(0, 45) + '...';
		}

		const color = colorMap[ds.id!];
		const dsId = ds.numericId!;

		return {
			text,
			icon: 'CircleFill',
			color,
			dsId,
		};
	}

	private addStoryToEditor(dsId: number, type: DataStoryType) {
		if (this.tour instanceof ReportsSelectDataStoryTour) {
			this.endTour();
		}

		const editor = this.editor;

		if (!editor) {
			return;
		}

		const dataSelect = this.blockSetup.getDataSelectById(dsId),
			order = this.visualization.getNextAvailableStoryOrder(),
			text = this.dataStoryService.prepareStoryHtmlForOrder(
				order,
				type,
				dataSelect && !dataSelect?.compares.length,
			);

		const dsMap = this.block.dsMap;
		dsMap[order] = dsId;
		this.block.dsMap = dsMap;

		editor.insertContent(text);

		if (this.block.isFromReport) {
			this.analyticsService.event(TrackingEvent.REPORT_METRIC_STORY_ADD, {
				type: 'metric_story',
			});
		}

		this.block.text = editor.getContent().trim();
		this.blockSetup.saveBlock();
	}

	private addStoryElementToEditor(type: StoryElementType, dsId: number): void {
		if (this.tour instanceof ReportsSelectDataElementTour) {
			this.endTour();
		}

		const editor = this.editor;

		let order = this.block.getDSMapPlaceholderForDsId(dsId);
		if (!order) {
			order = String(this.visualization.getNextAvailableStoryOrder());
			const dsMap = this.block.dsMap;
			dsMap[order] = dsId;
			this.block.dsMap = dsMap;
			this.blockSetup.saveBlock();
		}

		if (editor) {
			this.dataStoryService.addStoryElementToEditor(editor, type, +order);

			if (this.block.isFromReport) {
				this.analyticsService.event(TrackingEvent.REPORT_METRIC_STORY_ADD, {
					type: 'metric_element',
				});
			}
		}
	}

	private generateDataSelectSublistItems(
		colors = ThemeConfig[0]!.colors,
	): PopupListItem<string>[] {
		if (this.args.themeData.colors) {
			colors = this.args.themeData.colors;
		}

		const sublistItems: PopupListItem<string>[] = [];

		// add existing data selects as submenu items
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			const { text, icon, color: colorIndex, dsId } = this.getDataSelectSublistData(ds);

			sublistItems.push({
				id: `ds-${dsId}`,
				text,
				icon,
				color: colors[colorIndex as number],
				sublistItems: this.generateStoryElementSublistItems(dsId),
			});
		});

		sublistItems.push({
			id: 'new-metric',
			text: 'New metric',
			icon: 'Plus',
			color: '#0090ff',
			sublistItems: this.generateStoryElementSublistItems(),
		});

		return sublistItems;
	}

	private generateStoryElementSublistItems(dsId?: number): PopupListItem<string>[] {
		const subItems: PopupListItem<string>[] = [];

		this.dataStoryService.storyElementOrderInTinyMceDropdown.forEach((type) => {
			const text = this.dataStoryService.getStoryElementTitle(type);
			const action = dsId
				? `${CommandPopupAction.ADD_ELEMENT_PREFIX}${type}-${dsId}`
				: `${CommandPopupAction.ADD_METRIC_PREFIX}${type}`;

			subItems.push({
				id: `${dsId ? `ds-${dsId}-${type}` : 'new-metric'}-${type}`,
				text,
				action,
			});
		});

		return subItems;
	}

	private executeCommandPopupAction(editor: TinymceEditor, actionName: string): void {
		this.removeSlashBeforeCursor();

		// without focus and delay TinyMCE popups visually break (emoticons, table...)
		editor.focus();
		later(() => {
			// TinyMCE commands
			switch (actionName) {
				case CommandPopupAction.INSERT_TABLE:
					editor.execCommand('mceInsertTable', false, { rows: 2, columns: 2 });
					// also insert empty line to make navigating to next line easier
					editor.insertContent('<p>&nbsp;</p>');
					return;
				case CommandPopupAction.INSERT_LINK:
					editor.execCommand('mceLink');
					return;
				case CommandPopupAction.INSERT_DIVIDER:
					editor.execCommand('InsertHorizontalRule');
					return;
				case CommandPopupAction.INSERT_EMOTICON:
					editor.execCommand('mceEmoticons');
					return;
			}

			// data-story actions
			if (actionName.startsWith(CommandPopupAction.ADD_STORY_PREFIX)) {
				let dataStoryType;

				// map action names to DataStoryType enum values
				switch (actionName) {
					case CommandPopupAction.ADD_STORY_NEUTRAL:
						dataStoryType = this.visualization.defaultStoryType;
						break;
					case CommandPopupAction.ADD_STORY_GOOD:
						dataStoryType = DataStoryType.POSITIVITY_GOOD;
						break;
					case CommandPopupAction.ADD_STORY_OK:
						dataStoryType = DataStoryType.POSITIVITY_OK;
						break;
					case CommandPopupAction.ADD_STORY_BAD:
						dataStoryType = DataStoryType.POSITIVITY_BAD;
						break;
					default:
						dataStoryType = this.visualization.defaultStoryType;
				}

				this.createNewStoryFragmentAndAddToEditor(dataStoryType);
				return;
			}

			// story-element actions
			if (actionName.startsWith(CommandPopupAction.ADD_METRIC_PREFIX)) {
				const elementTypeName = actionName.replace(
					CommandPopupAction.ADD_METRIC_PREFIX,
					'',
				);

				if (elementTypeName) {
					this.visualization
						.addMetric()
						.then((dsId) => {
							this.addStoryElementToEditor(elementTypeName as StoryElementType, dsId);
						})
						.catch(
							this.requestService.showAllValidationErrors.bind(this.requestService),
						);
				}
				return;
			}

			// existing data select element
			if (actionName.startsWith(CommandPopupAction.ADD_ELEMENT_PREFIX)) {
				const [, , elementTypeName, dsIdString] = actionName.split('-');
				const dsId = parseInt(dsIdString!, 10);

				if (!isNaN(dsId) && elementTypeName) {
					this.addStoryElementToEditor(elementTypeName as StoryElementType, dsId);
				}
				return;
			}
		}, 100);
	}

	private removeSlashBeforeCursor(): void {
		const editor = this.editor;
		if (!editor) {
			return;
		}

		const selection = editor.selection,
			range = selection.getRng(),
			startContainer = range.startContainer,
			currentPosition = range.startOffset;

		// handle text nodes
		if (startContainer.nodeType === Node.TEXT_NODE) {
			const content = startContainer.textContent || '';
			if (currentPosition > 0 && content.charAt(currentPosition - 1) === '/') {
				range.setStart(startContainer, currentPosition - 1);
				range.setEnd(startContainer, currentPosition);
				selection.setRng(range);
				editor.execCommand('Delete');
			}
		}
		// handle element nodes
		else if (startContainer.nodeType === Node.ELEMENT_NODE) {
			// get all text content, including nested elements
			const walker = document.createTreeWalker(startContainer, NodeFilter.SHOW_TEXT, null);

			let node;
			while ((node = walker.nextNode())) {
				const nodeContent = node.textContent || '';
				if (nodeContent.includes('/')) {
					const lastSlashIndex = nodeContent.lastIndexOf('/');
					if (lastSlashIndex !== -1) {
						range.setStart(node, lastSlashIndex);
						range.setEnd(node, lastSlashIndex + 1);
						selection.setRng(range);
						editor.execCommand('Delete');
						break;
					}
				}
			}
		}
	}

	@action
	onChange(text: string): void {
		// since onChange from tinymce can trigger on unload, we check for visualization type
		// so we dont overwrite content again after switching away from data-story visualization type
		if (this.blockSetup.visualization.visualizationType !== BlockVisualization.DATA_STORY) {
			return;
		}

		const { ordersToKeep, cleanText } =
			this.dataStoryService.cleanupTextAndGetOrdersToKeep(text);

		// check if any story fragments were removed
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			const order = ds.getStoryOrder();
			if (typeof order === 'number' && !ordersToKeep.includes(order)) {
				this.loggerService.logToConsole('[Vis data-story] Deleting DS');
				this.blockSetup.deleteDataSelect(ds);
			}
		});

		const currentSavedText = this.block.text;
		if (cleanText !== currentSavedText) {
			this.block.text = cleanText;
			this.blockSetup.saveBlock();
		}
	}

	// ----- reports tour bubbles ----
	private checkTourStartConditions(): boolean {
		return isAlive(this) && !this.popupService.anyPopupsOpen && this.block.isFromReport;
	}

	private startEligibleTour(availableTours: string[]): void {
		const eligibleTourId = this.tourService.currentEligibleReportsDataStoryTourId;

		if (!this.tour && availableTours.includes(eligibleTourId)) {
			const tour = this.tourService.initTour(eligibleTourId);

			// we set the tour here, so that it won't wait to start to be initialized
			this.tour = tour;
			later(() => {
				if (this.checkTourStartConditions()) {
					tour?.start();
				} else {
					this.tour = null;
				}
			}, 500);
		}
	}

	private endTour(): void {
		const tour = this.tour;
		if (tour) {
			tour.end();
			this.tour = null;
		}
	}

	private completeSelectDataStoryTour(): void {
		if (this.tour instanceof ReportsSelectDataStoryTour) {
			this.endTour();
		}
	}

	// ----- common -----

	createStoryElementFor(order: number, type: string): StoryElementBase | null {
		// console.log('createStoryElementFor', order, type);

		const dsId = this.block.dsMap[order];

		const dataSelect = dsId
			? this.blockSetup.dataSelects.find((ds) => ds.numericId === dsId)
			: null;

		if (!dataSelect) {
			// console.log('no ds found !!!', order);
			return null;
		}

		const storyElement = this.dataStoryService.createStoryElementForType(type, {
			model: dataSelect,
		});

		if (storyElement) {
			storyElement.isEditable = !this.args.isUnselectable;
		}

		return storyElement!;
	}

	@action
	override setupView(node: HTMLElement): void {
		// initialize story element stuff (parent DataStoryBase)
		const storyContainerNode = node.querySelector<HTMLElement>('.story-container')!;
		super.setupView(storyContainerNode);

		// bind handler on adding new metric from settings panel
		this.eventManagerService.on(
			AppEvent.DESIGNER_UNIBLOCK_DATA_SELECT_ADDED,
			this,
			(eventData) => {
				const ds = eventData.dataSelect;
				if (ds.blockId === this.block.numericId) {
					const dsId = ds.numericId;
					if (dsId) {
						// set the cursor position to the end of the content
						// otherwise, will be added to the start
						const editor = this.editor;
						if (editor) {
							editor.selection.select(editor.getBody(), true);
							editor.selection.collapse(false);
							editor.focus();
						}
						// add the text
						this.addStoryToEditor(dsId, this.visualization.defaultStoryType);
					}
				}
			},
		);

		// bind handler on total content override - e.g. when just created the block
		this.eventManagerService.on(AppEvent.DESIGNER_DATA_STORY_OVERRIDE, this, (eventData) => {
			const block = eventData.block;
			if (block.id === this.block.id) {
				const content = block.text || '';
				this.editor?.setContent(content);
			}
		});

		const blockRenderer = this.args.blockRenderer;

		// bind content renderer handlers
		blockRenderer.registerStepHandler(BlockRenderStep.CONTENT, this, () => {
			// TODO something
			return Promise.resolve();
		});

		// add listener for table insert events
		this.eventManagerService.on(AppEvent.DATA_STORY_INSERT_TABLE, this, (eventData) => {
			if (eventData.block.id === this.block.id) {
				const editor = this.editor;

				if (editor) {
					editor.focus();
					// to remove default <p> before table, clear content first
					editor.setContent('');
					editor.execCommand('mceInsertTable', false, eventData.tableOptions);
				}
			}
		});

		// add listener for divider insert events
		this.eventManagerService.on(AppEvent.DATA_STORY_INSERT_DIVIDER, this, (eventData) => {
			if (eventData.block.id === this.block.id) {
				const editor = this.editor;

				if (editor) {
					editor.focus();
					// to remove default <p> before <hr>, just setting <hr> still adds it
					editor.setContent('');
					editor.insertContent('<hr>');
				}
			}
		});

		// add listener to set initial editor style format
		this.eventManagerService.on(AppEvent.DATA_STORY_SET_INITIAL_STYLE, this, (eventData) => {
			const { styleFormat, fragment } = eventData;

			if (styleFormat && fragment.blockId === this.block.numericId) {
				next(() => {
					if (this.editor) {
						this.editor.execCommand('mceToggleFormat', false, styleFormat);
					}
				});
			}
		});

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.CONTENT, this);
		});
	}

	private teardownFns: (() => void)[] = [];

	@action
	teardownView(): void {
		this.teardownFns.forEach((fn) => fn());

		this.eventManagerService.off(AppEvent.DESIGNER_UNIBLOCK_DATA_SELECT_ADDED, this);
		this.eventManagerService.off(AppEvent.DESIGNER_DATA_STORY_OVERRIDE, this);
		this.eventManagerService.off(AppEvent.DATA_STORY_INSERT_TABLE, this);
		this.eventManagerService.off(AppEvent.DATA_STORY_INSERT_DIVIDER, this);
		this.eventManagerService.off(AppEvent.DATA_STORY_SET_INITIAL_STYLE, this);
	}

	// ----- tooltip close fix -----
	// REPRODUCE: remove checkToCloseTooltip and handleClick, click "Add Data Story" twice and click on other block without clicking on their editor
	// ISSUE: the tooltip will stay on screen

	@action
	checkToCloseTooltip(): void {
		// the main issue was, that the tooltip would not close on selecting a different block and would stay on screen
		// so now we manually hide the tooltip when user loses permission to edit / deselecta the block

		const editor = this.editor;
		if (!editor) {
			return;
		}

		const isConnected = !!editor.container?.isConnected;
		if (isConnected && !this.args.canEdit) {
			editor.ui.hide();
		}
	}

	@action
	handleClick(_e: PointerEvent): void {
		// the main issue was, that the tooltip would not close on selecting a different block and would stay on screen
		// so now we manually show the tooltip when user gains permission to edit / selects the block

		next(() => {
			const editor = this.editor;

			// we check if the tooltip is not in the DOM, we don't do anything
			const isConnected = !!editor?.container?.isConnected;
			if (!isConnected) {
				return;
			}

			// if the tooltip is in the DOM, we show it on click / focus
			if (this.args.canEdit) {
				editor.ui.show();
			}
		});
	}
	<template>
		<div
			class="bc__html-content"
			data-hash={{this.dataHashesString}}
			role="button"
			{{didInsert this.setupView}}
			{{didUpdate this.checkToCloseTooltip @canEdit}}
			{{on "click" this.handleClick}}
			{{willDestroy this.teardownView}}
		>
			<div
				class="user-html story-container bgTheme-{{@themeData.bgTheme}}
					colorTheme-{{@themeData.themeId}}"
			>

				{{#if @isUnselectable}}

					{{{this.sanitizedText}}}

				{{else}}

					<FormHtmlEditorFullComponent
						@text={{this.visualization.shownText}}
						@onChange={{this.onChange}}
						@options={{this.editorOptions}}
					/>

				{{/if}}

			</div>

		</div>
	</template>
}
